/**
 * 图片压缩工具
 * 将图片压缩到指定大小（200KB）以内
 */

const MAX_SIZE = 200 * 1024; // 200KB
const MAX_WIDTH = 1920; // 最大宽度
const MAX_HEIGHT = 1920; // 最大高度
const QUALITY_STEP = 0.1; // 质量递减步长
const MIN_QUALITY = 0.3; // 最低质量

/**
 * 压缩图片到指定大小以内
 * @param file 原始图片文件
 * @returns 压缩后的Blob对象
 */
export async function compressImage(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // 计算压缩后的尺寸
        let width = img.width;
        let height = img.height;
        
        if (width > MAX_WIDTH || height > MAX_HEIGHT) {
          const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
          width = width * ratio;
          height = height * ratio;
        }
        
        // 创建canvas
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('无法创建canvas上下文'));
          return;
        }
        
        // 绘制图片
        ctx.drawImage(img, 0, 0, width, height);
        
        // 尝试不同的质量值，直到文件大小符合要求
        let quality = 0.9;
        let blob: Blob | null = null;
        
        const tryCompress = (): void => {
          canvas.toBlob(
            (compressedBlob) => {
              if (!compressedBlob) {
                reject(new Error('压缩失败'));
                return;
              }
              
              // 如果文件大小符合要求，或者质量已经降到最低，则返回
              if (compressedBlob.size <= MAX_SIZE || quality <= MIN_QUALITY) {
                resolve(compressedBlob);
                return;
              }
              
              // 继续降低质量
              quality = Math.max(MIN_QUALITY, quality - QUALITY_STEP);
              tryCompress();
            },
            'image/jpeg',
            quality
          );
        };
        
        tryCompress();
      };
      
      img.onerror = () => {
        reject(new Error('图片加载失败'));
      };
      
      if (e.target?.result) {
        img.src = e.target.result as string;
      }
    };
    
    reader.onerror = () => {
      reject(new Error('文件读取失败'));
    };
    
    reader.readAsDataURL(file);
  });
}

/**
 * 批量压缩图片
 * @param files 原始图片文件数组
 * @param onProgress 进度回调函数
 * @returns 压缩后的File对象数组
 */
export async function compressImages(
  files: File[],
  onProgress?: (current: number, total: number) => void
): Promise<File[]> {
  const compressedFiles: File[] = [];
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    
    try {
      // 如果文件已经小于200KB，直接使用原文件
      if (file.size <= MAX_SIZE) {
        compressedFiles.push(file);
        onProgress?.(i + 1, files.length);
        continue;
      }
      
      // 压缩图片
      const compressedBlob = await compressImage(file);
      
      // 创建新的File对象，保持原始文件名
      const compressedFile = new File(
        [compressedBlob],
        file.name,
        {
          type: 'image/jpeg',
          lastModified: Date.now()
        }
      );
      
      compressedFiles.push(compressedFile);
      onProgress?.(i + 1, files.length);
    } catch (error) {
      console.error(`压缩图片 ${file.name} 失败:`, error);
      // 压缩失败时使用原文件
      compressedFiles.push(file);
      onProgress?.(i + 1, files.length);
    }
  }
  
  return compressedFiles;
}

